import { useState } from 'react';
import { Link, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './state/auth';
import { AlertesProvider, Pastille, useAlertes } from './state/alertes';
import { IndicateurReseau, Squelette } from './components/ui';
import { exigencePour, menuPour, ongletsPour } from './components/menu';
import { Filet } from './components/Filet';
import { FileHorsLigne } from './components/FileHorsLigne';
import { MotDePasse } from './components/MotDePasse';
import { useMarque } from './state/marque';
import { SelecteurLangue } from './components/SelecteurLangue';
import { T } from './i18n';

import Connexion from './pages/Connexion';
import Taches from './pages/Taches';
import Disponibilites from './pages/Disponibilites';
import Planning from './pages/Planning';
import Locations from './pages/Locations';
import NouvelleReservation from './pages/NouvelleReservation';
import Reservation from './pages/Reservation';
import EtatDesLieux from './pages/EtatDesLieux';
import Caisse from './pages/Caisse';
import Capacite from './pages/Capacite';
import Analytique from './pages/Analytique';
import Flotte from './pages/Flotte';
import Clients from './pages/Clients';
import Journal from './pages/Journal';
import Corrections from './pages/Corrections';
import Cautions from './pages/Cautions';
import Entretien from './pages/Entretien';
import Amendes from './pages/Amendes';
import Sinistres from './pages/Sinistres';
import Factures from './pages/Factures';
import Parametres from './pages/Parametres';
import Utilisateurs from './pages/Utilisateurs';
import Suppression from './pages/Suppression';
import Aide from './pages/Aide';
import Plus from './pages/Plus';
import Contrats from './pages/Contrats';
import Assurances from './pages/Assurances';
import CaisseVehicule from './pages/CaisseVehicule';
import Notes from './pages/Notes';
import Depenses from './pages/Depenses';
import CaisseExtra from './pages/CaisseExtra';
import Dettes from './pages/Dettes';
import Banque from './pages/Banque';
import Echeances from './pages/Echeances';

/**
 * « Chacun son rôle » se voit d'abord ici.
 *
 * Sabrina n'a pas un menu réduit : elle a un AUTRE produit — une file de tâches,
 * aucune section stratégique. Ce qu'elle ne peut pas voir est absent, pas grisé.
 *
 * Les libellés et les regroupements vivent dans components/menu.ts, partagés
 * avec le menu du telephone : une seule source, aucun risque de divergence.
 */
function Navigation() {
  const { user, deconnexion, peut, estAgent } = useAuth();
  const [changeMdp, setChangeMdp] = useState(false);
  const marque = useMarque();
  const { alertes } = useAlertes();
  const { pathname } = useLocation();
  if (!user) return null;
  const groupes = menuPour(peut, estAgent);

  return (
    <nav className="nav">
      <div className="logo">{marque?.principal ?? '\u00A0'}<span className="accent">.</span><small>{marque?.secondaire}</small></div>

      {groupes.map((g) => (
        <div key={g.titre}>
          <div className="nav-sec">{g.titre}</div>
          {g.entrees.map((e) => {
            // Une branche s'ouvre quand on est dessus ou sur l'une de ses feuilles.
            const ouverte = Boolean(e.sous?.length)
              && (pathname === e.to || e.sous!.some((x) => pathname === x.to));
            return (
              <div key={e.to}>
                <NavLink to={e.to} end={e.to === '/'}
                         className={({ isActive }) => `${isActive ? 'actif' : ''}${alertes[e.to] ? ` alerte-${alertes[e.to]!.niveau}` : ''}`}>
                  <span aria-hidden="true" style={{ width: 18, display: 'inline-block' }}>{e.icone}</span>
                  {e.label}
                  <Pastille a={alertes[e.to]} />
                </NavLink>
                {ouverte && (
                  <div className="nav-sous">
                    {e.sous!.map((x) => (
                      <NavLink key={x.to} to={x.to}
                               className={({ isActive }) => `${isActive ? 'actif' : ''}${alertes[x.to] ? ` alerte-${alertes[x.to]!.niveau}` : ''}`}>
                        {x.label}
                        <Pastille a={alertes[x.to]} />
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div className="pied">
        <div className="qui"><b>{user.nom}</b><span>{user.roleLibelle}</span></div>
        <div className="actions">
          <button className="btn btn-f btn-s" onClick={() => setChangeMdp(true)}>{T.app.monMotDePasse}</button>
          <button className="btn btn-f btn-s" onClick={deconnexion}>{T.app.seDeconnecter}</button>
        </div>
        <SelecteurLangue compact />
      </div>
      {changeMdp && <MotDePasse onFermer={() => setChangeMdp(false)} />}
    </nav>
  );
}

function Onglets() {
  const { user, estAgent, peut } = useAuth();
  const { alertes } = useAlertes();
  if (!user) return null;
  // Sur telephone, tout ce qui vit derriere « Plus » doit quand meme se signaler.
  const visibles = ongletsPour(estAgent).filter((e) => !e.droit || peut(e.droit));
  const caches = Object.entries(alertes).filter(([chemin]) => !visibles.some((o) => o.to === chemin));
  const totalCache = caches.reduce((a, [, x]) => a + x.nombre, 0);
  const pireCache = caches.some(([, x]) => x.niveau === 'danger') ? 'danger' : 'attention';
  /*
   * Les onglets portent un droit comme les entrees de la barre laterale, mais
   * personne ne le lisait : Reda et Sabrina, qui n'ont pas `modifier.finance`,
   * voyaient un onglet « Banque » qui ne menait qu'a un refus.
   */
  const items = visibles;
  return (
    <div className="onglets" style={{ gridTemplateColumns: `repeat(${items.length},1fr)` }}>
      {items.map((e) => (
        <NavLink key={e.to} to={e.to} end={e.to === '/'}
                 className={({ isActive }) => (isActive ? 'actif' : '')}>
          <i aria-hidden="true">{e.icone}</i>{e.label}
          <Pastille a={e.to === '/plus'
            ? (totalCache > 0 ? { nombre: totalCache, niveau: pireCache as 'danger' | 'attention', detail: T.app.pointsARegarder(totalCache) } : undefined)
            : alertes[e.to]} />
        </NavLink>
      ))}
    </div>
  );
}

function Protege({ children }: { children: JSX.Element }) {
  const { user, chargement, peut, estAgent } = useAuth();
  const loc = useLocation();
  if (chargement) return <div style={{ padding: 40 }}><Squelette /></div>;
  if (!user) return <Navigate to="/connexion" state={{ from: loc }} replace />;

  /*
   * Un ecran cache dans le menu doit aussi etre refuse quand on tape son
   * adresse. Sans ce garde, l'ecran s'ouvrait, son chargement partait en 403,
   * et l'utilisateur restait devant un squelette gris, indefiniment, sans un
   * mot. Un logiciel qui ne repond pas est pire qu'un logiciel qui refuse.
   */
  const exigence = exigencePour(loc.pathname);
  const refuse = exigence
    && ((exigence.gerantsSeulement && estAgent) || (exigence.droit && !peut(exigence.droit)));

  if (refuse) {
    return (
      <div style={{ maxWidth: 520, margin: '48px auto', padding: '0 20px' }}>
        <div className="carte carte-p">
          <h2 style={{ marginBottom: 8 }}>{T.app.refuseTitre}</h2>
          <p style={{ color: 'var(--texte2)' }}>
            {exigence.gerantsSeulement || exigence.droit === 'voir.analytique'
              ? T.app.refuseAnalyses
              : T.app.refuseDroit}
          </p>
          <div className="actions" style={{ marginTop: 16 }}>
            <Link className="btn btn-p btn-l" to="/">{T.app.revenirTaches}</Link>
          </div>
        </div>
      </div>
    );
  }

  return children;
}

export default function App() {
  const { user, chargement, serveurInjoignable, reessayer } = useAuth();
  // Le filet est remonte a chaque changement d'ecran : sans cette cle, une fois
  // une erreur attrapee, le message restait affiche meme apres avoir clique
  // dans le menu — l'utilisateur croyait toute l'application cassee.
  const { pathname } = useLocation();

  if (chargement) return <div style={{ padding: 40, maxWidth: 520 }}><Squelette lignes={5} /></div>;

  // Session intacte, serveur muet : surtout pas l'ecran de connexion, qui ferait
  // retaper un mot de passe qui ne passera pas davantage.
  if (!user && serveurInjoignable) {
    return (
      <div style={{ maxWidth: 520, margin: '64px auto', padding: '0 20px' }}>
        <div className="carte carte-p">
          <h2 style={{ marginBottom: 8 }}>{T.app.serveurTitre}</h2>
          <p style={{ color: 'var(--texte2)' }}>
            {T.app.serveurConnecte}<b>{T.app.serveurPasLaPeine}</b>{T.app.serveurInjoignable}
          </p>
          <p style={{ color: 'var(--texte2)', marginTop: 10 }}>
            {T.app.serveurVerifiez}<b>{T.app.serveurAucunePerte}</b>
          </p>
          <div className="actions" style={{ marginTop: 16 }}>
            <button className="btn btn-p btn-l" onClick={reessayer}>{T.commun.actions.reessayer}</button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/connexion" element={<Connexion />} />
        <Route path="*" element={<Navigate to="/connexion" replace />} />
      </Routes>
    );
  }

  return (
    <AlertesProvider>
    <div className="app">
      <Navigation />
      <main className="principal">
        <IndicateurReseau />
        <FileHorsLigne />
        <Filet key={pathname}>
        <Routes>
          <Route path="/connexion" element={<Navigate to="/" replace />} />
          <Route path="/" element={<Protege><Taches /></Protege>} />
          <Route path="/disponibilites" element={<Protege><Disponibilites /></Protege>} />
          <Route path="/planning" element={<Protege><Planning /></Protege>} />
          <Route path="/locations" element={<Protege><Locations /></Protege>} />
          <Route path="/contrats" element={<Protege><Contrats /></Protege>} />
          <Route path="/reservations/nouvelle" element={<Protege><NouvelleReservation /></Protege>} />
          <Route path="/reservations/:id" element={<Protege><Reservation /></Protege>} />
          <Route path="/contrats/:id/etat-des-lieux/:sens" element={<Protege><EtatDesLieux /></Protege>} />
          <Route path="/caisse" element={<Protege><Caisse /></Protege>} />
          <Route path="/caisse/voiture/:id" element={<Protege><CaisseVehicule /></Protege>} />
          <Route path="/dettes" element={<Protege><Dettes /></Protege>} />
          <Route path="/depenses" element={<Protege><Depenses /></Protege>} />
          <Route path="/caisse-extra" element={<Protege><CaisseExtra /></Protege>} />
          <Route path="/banque" element={<Protege><Banque /></Protege>} />
          <Route path="/capacite" element={<Protege><Capacite /></Protege>} />
          <Route path="/analytique" element={<Protege><Analytique /></Protege>} />
          <Route path="/flotte" element={<Protege><Flotte /></Protege>} />
          <Route path="/clients" element={<Protege><Clients /></Protege>} />
          <Route path="/corrections" element={<Protege><Corrections /></Protege>} />
          <Route path="/cautions" element={<Protege><Cautions /></Protege>} />
          <Route path="/factures" element={<Protege><Factures /></Protege>} />
          <Route path="/entretien" element={<Protege><Entretien /></Protege>} />
          <Route path="/entretien/:cle" element={<Protege><Echeances mode="entretien" /></Protege>} />
          <Route path="/controle-technique" element={<Protege><Echeances mode="controle" /></Protege>} />
          <Route path="/assurances" element={<Protege><Assurances /></Protege>} />
          <Route path="/amendes" element={<Protege><Amendes /></Protege>} />
          <Route path="/sinistres" element={<Protege><Sinistres /></Protege>} />
          <Route path="/parametres" element={<Protege><Parametres /></Protege>} />
          <Route path="/utilisateurs" element={<Protege><Utilisateurs /></Protege>} />
          <Route path="/suppression" element={<Protege><Suppression /></Protege>} />
          <Route path="/aide" element={<Protege><Aide /></Protege>} />
          <Route path="/journal" element={<Protege><Journal /></Protege>} />
          <Route path="/notes" element={<Protege><Notes /></Protege>} />
          <Route path="/plus" element={<Protege><Plus /></Protege>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Filet>
      </main>
      <Onglets />
    </div>
    </AlertesProvider>
  );
}
